// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';

// --- RxJs
import { Observable, BehaviorSubject, combineLatest, Subscription } from 'rxjs';

// --- HS Code
import {
  ContextQuestionModel,
  SurveyResponseModel,
  QuestionGroupModel,
  QuestionResponseModel,
  QuestionModel,
  QuestionTypes,
  Constants,
  SurveyResponseTypes,
  SurveyResponseContexts,
  SurveyStatuses
} from '../../../models';
import { QuestionsBaseService } from '../questions/questions-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export abstract class ResponseBaseService implements OnDestroy {
  private baseSubs: Subscription;
  private _questionResponseGroups: QuestionGroupModel[][];
  private _questionResponseGroups$: BehaviorSubject<QuestionGroupModel[][]>;
  get questionResponseGroups$(): Observable<QuestionGroupModel[][]> {
    return this._questionResponseGroups$;
  }

  private _currentQuestionResponseGroup$: BehaviorSubject<QuestionGroupModel[]>;
  get currentQuestionResponseGroup$(): Observable<QuestionGroupModel[]> {
    return this._currentQuestionResponseGroup$;
  }

  protected _indicatorResponses$: BehaviorSubject<QuestionResponseModel[]>;
  get indicatorResponses$(): Observable<QuestionResponseModel[]> {
    return this._indicatorResponses$;
  }

  protected _groupIndex$: BehaviorSubject<number>;
  get groupIndex$(): Observable<number> {
    return this._groupIndex$;
  }

  protected _currentQuestions$: BehaviorSubject<QuestionModel[]>;
  get currentQuestions$(): Observable<QuestionModel[]> {
    return this._currentQuestions$;
  }

  protected _activeResponse$: BehaviorSubject<SurveyResponseModel>;
  get activeResponse$(): Observable<SurveyResponseModel> {
    return this._activeResponse$;
  }

  protected _surveyProgress$: BehaviorSubject<number>;
  get surveyResponseProgress$(): Observable<number> {
    return this._surveyProgress$;
  }

  constructor(private questionService: QuestionsBaseService) {
    this._activeResponse$ = new BehaviorSubject<SurveyResponseModel>(null);
    this._questionResponseGroups$ = new BehaviorSubject<QuestionGroupModel[][]>(null);
    this._currentQuestionResponseGroup$ = new BehaviorSubject<QuestionGroupModel[]>(null);
    this._currentQuestions$ = new BehaviorSubject<QuestionModel[]>(null);
    this._groupIndex$ = new BehaviorSubject<number>(null);
    this._indicatorResponses$ = new BehaviorSubject<QuestionResponseModel[]>(
      null
    );
    this._activeResponse$ = new BehaviorSubject<SurveyResponseModel>(null);
    this._surveyProgress$ = new BehaviorSubject<number>(0);
  }

  ngOnDestroy() {
    if (this.baseSubs != null) {
      this.baseSubs.unsubscribe();
      this.baseSubs = null;
    }
  }

  abstract loadResponse(surveyIdentifier: string): void;
  abstract updateStatus(model: SurveyResponseModel): void;
  abstract updateComment(name: string, value: string): void;
  abstract update(model: SurveyResponseModel): Observable<boolean>;
  abstract questionAnswered(response: ContextQuestionModel): void;
  abstract onNext(resposne: SurveyResponseModel): void;

  protected updateProgress(): void {
    const response = this._activeResponse$.value;
    if (response) {
      let contextMultiplier = 1;
      if (response.responseContext === SurveyResponseContexts.participant) {
        contextMultiplier = 2;
      }
      let progressAdded = 0;
      if (response.status > SurveyStatuses.commentsQuestions) {
        progressAdded = Constants.ONE_ADDED_TO_SHOW_COMMENTS_QUESTION_PROGRESS;
      }
      const answeredQuestions = response.questionResponses.filter(qa => typeof qa.response !== 'undefined' && qa.response !== null).length +
                                progressAdded;
      const totalQuestions = (Constants.NUMBER_OF_INDICATOR_QUESTIONS * contextMultiplier) +
        Constants.NUMBER_OF_EFFECTIVENESS_QUESTIONS +
        Constants.ONE_ADDED_TO_SHOW_COMMENTS_QUESTION_PROGRESS;
      this._surveyProgress$.next(answeredQuestions * 100 / totalQuestions);
    }
  }

  protected initialiseQuestionResponses(
    questionResponses: QuestionResponseModel[]
  ): void {
    this._indicatorResponses$.next([] as QuestionResponseModel[]);
    this._groupIndex$.next(0);
    this._currentQuestions$.next([] as QuestionModel[]);

    this.baseSubs = this.questionService.questions$.subscribe((questions) => {
      if (!questions) {
        return;
      }

      this._questionResponseGroups = new Array<QuestionGroupModel[]>();
      for (let i = 0;
        i < questions.filter(q => q.questionType === QuestionTypes.indicator).length;
        i += Constants.NUMBER_OF_QUESTIONS_PER_INDICATOR_GROUP) {
          this._questionResponseGroups.push(new Array<QuestionGroupModel>());

          for (let j = 0; j < Constants.NUMBER_OF_QUESTIONS_PER_INDICATOR_GROUP; j++) {

            const questionResponseGroup =
              this._createResponseGroup(questions.filter(q => q.questionType === QuestionTypes.indicator)[i + j], questionResponses);

            this._questionResponseGroups[Math.floor(i / Constants.NUMBER_OF_QUESTIONS_PER_INDICATOR_GROUP)].push(questionResponseGroup);
          }
      }

      this._questionResponseGroups[5] = new Array<QuestionGroupModel>();
      for (const question of questions.filter(q => q.questionType === QuestionTypes.effectiveness)) {
        const questionResponseGroup = this._createResponseGroup(question, questionResponses);
        this._questionResponseGroups[5].push(questionResponseGroup);
      }

      const sortedResponses = questionResponses.sort((a, b) => {
        const q1 = questions.find(q => q.id.toString() === a.questionId);
        const q2 = questions.find(q => q.id.toString() === b.questionId);

        if (!q1 || !q2) {
          return 0;
        }
        return ((q1.questionType - 1) * Constants.NUMBER_OF_INDICATOR_QUESTIONS + q1.index) -
               ((q2.questionType - 1) * Constants.NUMBER_OF_INDICATOR_QUESTIONS + q2.index);
      });
      const firstUnanswered = sortedResponses
        .find(qr => typeof qr.response === 'undefined' || qr.response === null);
      const unansweredQuestion = questions.find(q => firstUnanswered && q.id.toString() === firstUnanswered.questionId);
      let currentGroupIndex = 0;
      if (unansweredQuestion) {
        currentGroupIndex = Math.floor(
          (((unansweredQuestion.questionType - 1) * Constants.NUMBER_OF_INDICATOR_QUESTIONS) +
          (unansweredQuestion.index - 1)) / Constants.NUMBER_OF_QUESTIONS_PER_INDICATOR_GROUP);
      } else {
        currentGroupIndex = 5;
      }
      if (currentGroupIndex < 0) {
        currentGroupIndex = 0;
      } else if (currentGroupIndex > 5) {
        currentGroupIndex = 5;
      }

      if (currentGroupIndex === 5) {
        if (this._activeResponse$.value && this._activeResponse$.value.status < SurveyStatuses.effectivenessQuestions) {
          const activeResponse = this._activeResponse$.value;
          activeResponse.status = SurveyStatuses.effectivenessQuestions;
          this._activeResponse$.next(activeResponse);
        }
      }

      this._questionResponseGroups$.next(this._questionResponseGroups);
      this._groupIndex$.next(currentGroupIndex);
      this._currentQuestionResponseGroup$.next(this._questionResponseGroups[currentGroupIndex]);
    });

    this.questionService.loadQuestions();
  }

  private _createResponseGroup(question: QuestionModel, questionResponses: QuestionResponseModel[]): QuestionGroupModel {
    const groupAnswers = questionResponses.filter(qr => qr.questionId === question.id.toString());
    const isAnswered = groupAnswers.every(ga => typeof ga.response !== 'undefined' &&
     ga.response !== null &&
     ga.response >= 0 && ga.response <= 4);

    const questionResponseGroup = {
      questionId: question.id,
      index: question.index,
      isAnswered,
      text: question.text,
      questions: [] as ContextQuestionModel[]
    } as QuestionGroupModel;

    for (const answer of groupAnswers.sort((a, b) => a.responseType - b.responseType)) {
      let answerTitle: string;
      switch (answer.responseType) {
        case SurveyResponseTypes.self:
          answerTitle = 'responseTypeSelfScore';
          break;
        case SurveyResponseTypes.benchmark:
          answerTitle = 'responseTypeBenchmark';
          break;
        default:
          answerTitle = 'responseTypeSelfScore';
          break;
      }
      questionResponseGroup.questions.push({
        surveyId: answer.surveyId,
        questionId: question.id,
        responseId: answer.responseId,
        questionResponseId: answer.id,
        responseType: answer.responseType,
        title: answerTitle,
        response: answer.response
      } as ContextQuestionModel);
    }

    return questionResponseGroup;
  }
}
